import {Type, type} from 'arktype';
import {Predicate} from 'effect/Predicate';
import * as PathToRegexp from 'path-to-regexp';
import {Action} from 'redux';
import {
    createDestination,
    createNextLocation,
    getDefaultLocation,
    Location,
    NavigationType,
    Path,
    To,
} from './BrowserHistory';

interface HistoryEntry {
    location: Location;
    navigationType: NavigationType;
}
export interface HistoryStack extends HistoryEntry {
    following?: HistoryStack;
    preceding?: HistoryStack;
}

export namespace HistoryStack {
    export const reduce = reduceHistoryStack;
    export const init = getInitialHistoryStack;

    export const toArray = getLocationsFromStack;
    export const fromArray = getStackFromLocations;

    export const replacer = createJsonReplacer;
    export const reviver = createJsonReviver;

    export const codec = createPersistenceCodec;
}

export interface Destination extends Partial<Path> {
    state?: unknown;
    key?: string;
}

export const historyGo = (type => {
    const create = (delta: number) => ({delta, type});
    create.type = type;
    create.match = (a: Action): a is ReturnType<typeof create> => a.type === type;
    return create;
})('history/go' as const);

export const historyPush = (type => {
    const create = (destination: Destination) => ({...destination, type});
    create.type = type;
    create.match = (a: Action): a is ReturnType<typeof create> => a.type === type;
    return create;
})('history/push' as const);

export const historyReplace = (type => {
    const create = (destination: Destination) => ({...destination, type});
    create.type = type;
    create.match = (a: Action): a is ReturnType<typeof create> => a.type === type;
    return create;
})('history/replace' as const);

export const historyUpdate = (type => {
    const create = (stack: HistoryEntry) => ({
        stack,
        type: `${type}${stack.navigationType}` as const,
    });
    type ThisAction = ReturnType<typeof create>;
    create.match = (a: Action): a is ThisAction => a.type.startsWith(type);
    create.isPop = (a: Action): a is ThisAction => create.match(a) && a.stack.navigationType === NavigationType.Pop;

    create.isTo = (pattern: string, whereParams: Record<string, Predicate<string>> = {}) => {
        const doMatch = PathToRegexp.match(pattern);
        return (a: Action): a is ThisAction => {
            if (!create.match(a)) {
                return false;
            }
            const pathname = a.stack.location.pathname;
            const match = doMatch(pathname);
            if (!match) {
                return false;
            }
            const {params} = match;
            const keys = Object.keys(whereParams);
            for (const key of keys) {
                const [param] = [params[key]].flat();
                if (!param) {
                    return false;
                }
                const predicate = whereParams[key];
                if (!predicate(param)) {
                    return false;
                }
            }
            return true;
        };
    };

    return create;
})('history/update/' as const);

/**
 * @deprecated @see {@link historyPush}
 */
export function push(to: To, state?: unknown) {
    return historyPush(createDestination(to, state));
}

export type HistoryStackAction =
    | ReturnType<typeof historyGo>
    | ReturnType<typeof historyPush>
    | ReturnType<typeof historyReplace>
    | ReturnType<typeof historyUpdate>;

export function getInitialHistoryStack(
    currentLocation = getDefaultLocation(),
    persistedStack?: HistoryStack | null
): HistoryStack {
    return {
        ...persistedStack,
        navigationType: NavigationType.Pop,
        location: currentLocation,
    };
}

export function reduceHistoryStack(entry: HistoryStack, action: HistoryStackAction): HistoryStack {
    if (historyGo.match(action)) {
        return reduceHistoryGo(entry, action.delta);
    }
    if (historyReplace.match(action)) {
        return reduceHistoryReplace(entry, action);
    }
    if (historyPush.match(action)) {
        return reduceHistoryPush(entry, action);
    }
    if (historyUpdate.match(action)) {
        return action.stack;
    }
    return entry;
}

function reduceHistoryPush(entry: HistoryStack, to: Destination) {
    const preceding = {...entry};
    const current: HistoryStack = {
        preceding,
        location: createNextLocation(entry.location, to, {indexDelta: 1}),
        navigationType: NavigationType.Push,
    };
    preceding.following = current;
    return current;
}

function reduceHistoryReplace(entry: HistoryStack, to: Destination) {
    const current: HistoryStack = {
        ...entry,
        location: createNextLocation(entry.location, to, {indexDelta: 0}),
        navigationType: NavigationType.Replace,
    };
    return current;
}

function reduceHistoryGo(entry: HistoryStack, delta: number) {
    let next = entry;
    if (delta < 0) {
        next = getPreceding(entry, delta);
    }
    if (delta > 0) {
        next = getFollowing(entry, delta);
    }
    if (next !== entry) {
        next = {...next, navigationType: NavigationType.Pop};
    }
    return next;
}

function getPreceding(entry: HistoryStack, delta: number) {
    if (delta >= 0 || !entry.preceding) {
        return entry;
    }
    return getPreceding(entry.preceding, delta + 1);
}

function getFollowing(entry: HistoryStack, delta: number) {
    if (delta <= 0 || !entry.following) {
        return entry;
    }
    return getFollowing(entry.following, delta - 1);
}

function getLocationsFromStack(starting: HistoryStack) {
    const array: Location[] = [];
    let entry: HistoryStack | undefined = starting;
    while (entry) {
        array.push(entry.location);
        entry = entry.preceding;
    }
    array.reverse();
    return array;
}

function getStackFromLocations([first, ...locations]: Location[]) {
    let entry: HistoryStack = {
        navigationType: NavigationType.Push,
        location: first,
    };
    for (const location of locations) {
        entry.following = {
            navigationType: NavigationType.Push,
            location,
            preceding: entry,
        };
        entry = entry.following;
    }
    return entry;
}

function createJsonReplacer({underKey, lengthLimit = Infinity}: {underKey: string; lengthLimit?: number}) {
    return (key: string, value: any) => {
        if (key === underKey && HisotyrStackSchema.allows(value)) {
            let locations = HistoryStack.toArray(value);
            if (locations.length > lengthLimit) {
                locations = locations.slice(locations.length - lengthLimit);
            }
            return {
                data: locations,
                [typeKey]: typeName,
            } satisfies typeof HistoryViewWrapper.infer;
        }
        return value;
    };
}

function createJsonReviver({underKey}: {underKey: string}) {
    return (key: string, value: any) => {
        if (key === underKey && UnknownWrapper.allows(value)) {
            const locations = HistoryViewWrapper.get('data').assert(value.data);
            return HistoryStack.fromArray(locations);
        }
        return value;
    };
}

const LocationSchema = type({
    pathname: 'string',
    search: 'string',
    hash: 'string',
    state: 'unknown',
    key: 'string',
    index: 'number',
});

const HisotyrStackSchema: Type<HistoryStack> = type({
    navigationType: type.enumerated(NavigationType.Pop, NavigationType.Push, NavigationType.Replace),
    location: LocationSchema,
});

/**
 * Special key supported by redux devtools to show
 * {@link https://cloud.githubusercontent.com/assets/7957859/21814330/a17d556a-d761-11e6-85ef-159dd12f36c5.png nice preview}
 */
const typeKey = '__serializedType__';
const typeName = 'HistoryStack';

const Wrapper = type('<t>', {
    data: 't',
    [typeKey]: `"${typeName}"`,
});

const UnknownWrapper = Wrapper('unknown');
const HistoryViewWrapper = Wrapper(LocationSchema.array());

function createPersistenceCodec({lengthLimit}: {lengthLimit: number}) {
    return {
        decode(string: string): HistoryStack {
            const {data: locations} = HistoryViewWrapper.assert(JSON.parse(string));
            return HistoryStack.fromArray(locations);
        },
        encode(stack: HistoryStack) {
            return JSON.stringify(stack, HistoryStack.replacer({underKey: '', lengthLimit}));
        },
    };
}
